import React, { useCallback, useMemo, useRef } from "react";
import { Plus, Trash2 } from "lucide-react";
import { InputNumber } from "components";

export interface VoucherItemColumn<T> {
  /** Column key from item object */
  key: keyof T | string;
  /** Column header */
  header: string;
  /** Column width (min-width) */
  width?: number;
  /** Flex grow */
  flex?: number;
  /** Text alignment */
  align?: "left" | "center" | "right";
  /** Whether column is editable */
  editable?: boolean;
  /** Input type for editable columns */
  inputType?: "text" | "number" | "select" | "custom";
  /** For number inputs */
  numberOptions?: {
    suffix?: string;
    decimalScale?: number;
    allowNegative?: boolean;
  };
  /** For select inputs */
  selectOptions?: Array<{ value: string | number; label: string }>;
  /** Custom cell renderer */
  cellRenderer?: (
    item: T,
    index: number,
    onChange: (value: any) => void,
  ) => React.ReactNode;
  /** Value formatter for display */
  valueFormatter?: (value: any, item: T) => string;
}

export interface VoucherItemsTableProps<T> {
  /** Column definitions */
  columns: VoucherItemColumn<T>[];
  /** Items array */
  items: T[];
  /** Callback when items change */
  onChange: (items: T[]) => void;
  /** Callback to add new item */
  onAddItem?: () => void;
  /** Show add button */
  showAddButton?: boolean;
  /** Add button label */
  addButtonLabel?: string;
  /** Minimum items required (disables delete if at minimum) */
  minItems?: number;
  /** Row key getter */
  getRowKey?: (item: T, index: number) => string;
  /** Table title */
  title?: string;
  /** Show total row */
  showTotal?: boolean;
  /** Total amount */
  totalAmount?: number;
  /** Empty state message */
  emptyMessage?: string;
  /** Enable Tab navigation between cells */
  enableTabNavigation?: boolean;
}

/**
 * Reusable table component for voucher/order items.
 * Features:
 * - Tab navigation between cells
 * - Enter to add new row
 * - Inline editing with number/text/select inputs
 * - Delete row confirmation
 */
function VoucherItemsTable<T extends Record<string, any>>({
  columns,
  items,
  onChange,
  onAddItem,
  showAddButton = true,
  addButtonLabel = "Thêm dòng",
  minItems = 1,
  getRowKey,
  title = "Chi tiết",
  showTotal = true,
  totalAmount,
  emptyMessage = "Chưa có dòng chi tiết. Thêm mới để bắt đầu.",
  enableTabNavigation = true,
}: VoucherItemsTableProps<T>) {
  const tableRef = useRef<HTMLTableElement>(null);

  // Handle item field change
  const handleItemChange = useCallback(
    (index: number, key: string, value: any) => {
      const newItems = items.map((item, idx) =>
        idx === index ? { ...item, [key]: value } : item,
      );
      onChange(newItems);
    },
    [items, onChange],
  );

  // Handle remove item
  const handleRemoveItem = useCallback(
    (index: number) => {
      if (items.length <= minItems) return;
      const newItems = items.filter((_, idx) => idx !== index);
      onChange(newItems);
    },
    [items, minItems, onChange],
  );

  // Tab navigation between editable cells
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, rowIndex: number, _colIndex: number) => {
      if (!enableTabNavigation) return;

      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        // If on last row, add new item
        if (rowIndex === items.length - 1 && onAddItem) {
          onAddItem();
        }
      }
    },
    [enableTabNavigation, items.length, onAddItem],
  );

  // Calculate default row key
  const defaultGetRowKey = useCallback((item: T, index: number): string => {
    if ((item as any).__tempId) return (item as any).__tempId;
    if ((item as any).id) return String((item as any).id);
    return `row-${index}`;
  }, []);

  const rowKeyGetter = getRowKey || defaultGetRowKey;

  // Render cell based on column configuration
  const renderCell = useCallback(
    (
      column: VoucherItemColumn<T>,
      item: T,
      rowIndex: number,
      colIndex: number,
    ) => {
      const value = item[column.key as keyof T];
      const handleChange = (newValue: any) =>
        handleItemChange(rowIndex, column.key as string, newValue);

      // Custom renderer
      if (column.cellRenderer) {
        return column.cellRenderer(item, rowIndex, handleChange);
      }

      // Non-editable - just display value
      if (!column.editable) {
        const displayValue = column.valueFormatter
          ? column.valueFormatter(value, item)
          : value;
        return (
          <span className="text-gray-900 truncate block">
            {displayValue ?? ""}
          </span>
        );
      }

      // Number input
      if (column.inputType === "number") {
        return (
          <InputNumber
            value={Number(value) || 0}
            onValueChange={(values) => handleChange(values.floatValue ?? 0)}
            className="h-9 w-full px-2 py-1 text-right text-sm"
            suffix={column.numberOptions?.suffix}
            decimalScale={column.numberOptions?.decimalScale ?? 0}
            onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
          />
        );
      }

      // Select input
      if (column.inputType === "select" && column.selectOptions) {
        return (
          <select
            value={String(value ?? "")}
            onChange={(e) => handleChange(e.target.value)}
            onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
            className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
          >
            <option value="">Chọn...</option>
            {column.selectOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        );
      }

      // Default: text input
      return (
        <input
          type="text"
          value={String(value ?? "")}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
          className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
        />
      );
    },
    [handleItemChange, handleKeyDown],
  );

  // Memoize column styles
  const columnStyles = useMemo(
    () =>
      columns.map((col) => ({
        minWidth: col.width || 120,
        flex: col.flex !== undefined ? col.flex : 1,
        textAlign: col.align || "left",
      })),
    [columns],
  );

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 flex-wrap gap-2">
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        <div className="flex items-center gap-3">
          {showTotal && totalAmount !== undefined && (
            <div className="text-right">
              <p className="text-xs text-gray-500">Tổng tiền</p>
              <p className="text-base font-semibold text-gray-900">
                {totalAmount.toLocaleString("vi-VN")} đ
              </p>
            </div>
          )}
          {showAddButton && onAddItem && (
            <button
              type="button"
              onClick={onAddItem}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Plus className="h-4 w-4" />
              {addButtonLabel}
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table
          ref={tableRef}
          className="min-w-full divide-y divide-gray-200 text-sm"
        >
          <thead className="bg-gray-50 text-gray-700">
            <tr>
              <th className="px-3 py-2.5 text-left font-semibold w-12">STT</th>
              {columns.map((col, idx) => (
                <th
                  key={String(col.key)}
                  className={`px-3 py-2.5 font-semibold ${
                    col.align === "right"
                      ? "text-right"
                      : col.align === "center"
                        ? "text-center"
                        : "text-left"
                  }`}
                  style={{ minWidth: columnStyles[idx].minWidth }}
                >
                  {col.header}
                </th>
              ))}
              <th className="px-3 py-2.5 text-center font-semibold w-20">
                Thao tác
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {items.length > 0 ? (
              items.map((item, rowIndex) => (
                <tr
                  key={rowKeyGetter(item, rowIndex)}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-3 py-2 text-gray-500 w-12">
                    {rowIndex + 1}
                  </td>
                  {columns.map((col, colIndex) => (
                    <td
                      key={String(col.key)}
                      className={`px-3 py-2 ${
                        col.align === "right"
                          ? "text-right"
                          : col.align === "center"
                            ? "text-center"
                            : "text-left"
                      }`}
                    >
                      {renderCell(col, item, rowIndex, colIndex)}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-center">
                    <button
                      type="button"
                      onClick={() => handleRemoveItem(rowIndex)}
                      disabled={items.length <= minItems}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                      Xóa
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length + 2}
                  className="px-4 py-8 text-center text-gray-500"
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer with total */}
      {showTotal && totalAmount !== undefined && items.length > 0 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50">
          <p className="text-sm text-gray-600">
            Tổng cộng:{" "}
            <span className="font-semibold text-gray-900">
              {totalAmount.toLocaleString("vi-VN")} đ
            </span>
          </p>
        </div>
      )}
    </div>
  );
}

export default VoucherItemsTable;
